import { useReactiveVar } from '@apollo/client';
import { StylesProvider } from '@mui/styles';
import { useEffect, useMemo, useReducer } from 'react';

import './App.scss';
import { useTranslation } from 'react-i18next';

import ApolloConfig from './apollo/ApolloConfig';
import { routeResetVar } from './apollo/timeout/timeoutManager';
import ChatBotWidget from './components/generics/ChatBotWidget/ChatBotWidget';
import ListNotificationComponent from './components/generics/ListNotificationComponent/ListNotificationComponent';
import { LoaderComponent } from './components/generics/LoaderComponent/LoaderComponent';
import ModalView from './components/generics/ModalComponent/ModalView';
import ScreenReaderLiveRegion from './components/generics/ScreenReaderLiveRegion/ScreenReaderLiveRegion';
import SessionTimeoutProvider from './components/generics/SessionTimeoutProvider/SessionTimeoutProvider';

import MixpanelOnRouteChange from './components/mixpanel/MixpanelOnRouteChange';
import { ProgressBarStyle } from './components/template-minimal/LoadingScreenComponent/LoadingScreen';
import RtlLayout from './components/template-minimal/RtlLayout';
import ScrollToTop from './components/template-minimal/ScrollToTop';
import { AuthContextProvider } from './contexts/AuthContext/AuthContext';
import { FeatureFlagsContext } from './contexts/FeatureFlagsContext';
import { InstitutionCustomTextsProvider } from './contexts/InstitutionCustomTextContext';
import featureFlagReducer, { featuresFlagReducerState } from './contexts/reducer/featureFlagsReducer';
import { SettingsProvider } from './contexts/SettingsContext';
import StudentDataContextProvider from './contexts/StudentDataContext/StudentDataContext';
import { StudentOverviewProvider } from './contexts/StudentOverviewContext';
import Routes from './routes/Routes';
import ThemeConfig from './theme';
import GlobalStyles from './theme/globalStyles';
import { PageTimeoutScreen } from './views/Errors';

const App: React.FC = () => {
  const [state, dispatch] = useReducer(featureFlagReducer, featuresFlagReducerState);
  const { i18n } = useTranslation();
  // Bumped when the user retries after a network timeout, to remount the route
  // subtree so its queries re-run. See apollo/timeout/timeoutManager.
  const routeResetKey = useReactiveVar(routeResetVar);

  useEffect(() => {
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  const value = useMemo(
    () => ({
      features: state.features,
      setFeatures: (initialValue: string[]) => {
        dispatch({ type: 'addFeatures', initialValue });
      },
    }),
    [state.features],
  );

  return (
    <ApolloConfig>
      <SettingsProvider>
        <StylesProvider>
          <FeatureFlagsContext.Provider value={value}>
            <AuthContextProvider dispatch={dispatch}>
              <SessionTimeoutProvider isEnabled={process.env.REACT_APP_TIMEOUT_ENABLED !== 'false'}>
                <StudentDataContextProvider>
                  <StudentOverviewProvider>
                    <InstitutionCustomTextsProvider>
                      <ThemeConfig>
                        <RtlLayout>
                          <GlobalStyles />
                          <ProgressBarStyle />
                          <MixpanelOnRouteChange />
                          <ScrollToTop />
                          <Routes key={routeResetKey} />
                          <ModalView />
                          <PageTimeoutScreen />
                          <LoaderComponent />
                          <ListNotificationComponent />
                          <ScreenReaderLiveRegion />
                          <ChatBotWidget />
                        </RtlLayout>
                      </ThemeConfig>
                    </InstitutionCustomTextsProvider>
                  </StudentOverviewProvider>
                </StudentDataContextProvider>
              </SessionTimeoutProvider>
            </AuthContextProvider>
          </FeatureFlagsContext.Provider>
        </StylesProvider>
      </SettingsProvider>
    </ApolloConfig>
  );
};

export default App;
